import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X, Building2 } from "lucide-react";
import { useState, useEffect } from "react";
import { daanAmrutLogo } from "@/project/data/sample";
import { AISearch } from "./ai-search";

const logoUrl = daanAmrutLogo;

const nav = [
  { to: "/initiatives", label: "Initiatives" },
  { to: "/organisations", label: "Orgs" },
  { to: "/campaigns", label: "Campaigns" },
  { to: "/emergency", label: "SOS Rescue" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
] as const;

export function SiteHeader() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <>
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl shadow-[0_1px_12px_rgba(0,0,0,0.05)]">
        {/* ── Main Nav Bar ──────────────────────────────────────── */}
        <div className="border-b border-border/60">
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">

            {/* ── Logo ──────────────────────────────────────────── */}
            <Link to="/" className="flex shrink-0 items-center gap-2.5 group">
              <img
                src={logoUrl}
                alt="Daan Amrut Foundation"
                className="h-10 w-auto shrink-0 object-contain"
              />
              <span className="hidden xl:flex flex-col justify-center leading-tight border-l border-border pl-3">
                <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground/70">
                  Daan Amrut Foundation
                </span>
                <span className="whitespace-nowrap font-display text-sm font-extrabold tracking-tight text-[var(--maroon-deep)]">
                  Seva. Transparency. Impact.
                </span>
              </span>
            </Link>

            {/* ── Flat Nav (desktop) ────────────────────────────── */}
            <nav className="hidden lg:flex min-w-0 items-center gap-0.5 text-sm">
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  activeOptions={{ exact: false }}
                  activeProps={{ className: "text-[var(--maroon)] font-bold bg-[var(--cream-2)] rounded-lg px-3 py-2 whitespace-nowrap transition-colors" }}
                  inactiveProps={{ className: "text-foreground/75 hover:text-[var(--maroon)] font-medium rounded-lg px-3 py-2 whitespace-nowrap transition-colors hover:bg-[var(--cream-2)]" }}
                >
                  {n.label}
                </Link>
              ))}
            </nav>

            {/* ── Right Actions ─────────────────────────────────── */}
            <div className="flex items-center gap-2">
              {/* Register Gaushala CTA */}
              <Link
                to="/register-institution"
                className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[var(--saffron-deep)]/30 bg-[var(--saffron)]/15 px-3.5 py-2 text-xs font-bold text-[var(--maroon-deep)] hover:bg-[var(--saffron)]/30 transition"
                title="List your Panjrapol, Gaushala or shelter"
              >
                <Building2 className="h-3.5 w-3.5 shrink-0" />
                <span className="whitespace-nowrap">Register Gaushala</span>
              </Link>

              {/* Demo Login */}
              <Link
                to="/demo"
                className="hidden xl:block rounded-full border border-border px-3.5 py-2 text-xs font-medium text-foreground/80 hover:bg-muted/50 transition"
              >
                Demo Login
              </Link>

              {/* Donate CTA */}
              <Link
                to="/donate"
                className="rounded-full bg-[var(--maroon)] px-4 py-2 text-xs sm:text-sm font-semibold text-[var(--cream)] shadow-sm hover:bg-[var(--maroon-deep)] transition active:scale-95"
              >
                Donate Now
              </Link>

              {/* Hamburger */}
              <button
                aria-label="Menu"
                className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-border bg-background hover:bg-muted transition"
                onClick={() => setMenuOpen((v) => !v)}
              >
                {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* ── AI Search Bar (always visible below header) ──────── */}
        <div className="border-b border-border/40 bg-[var(--cream-2)]/60 px-4 py-2 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <AISearch compact />
          </div>
        </div>

        {/* ── Mobile Nav Dropdown ───────────────────────────────── */}
        {menuOpen && (
          <div className="lg:hidden border-t border-border/60 bg-background animate-in slide-in-from-top-1 duration-150 max-h-[80vh] overflow-y-auto">
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">

              {/* Mobile Search */}
              <div className="mb-1">
                <AISearch />
              </div>

              {/* Nav Links */}
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-[var(--cream-2)] transition"
                >
                  {n.label}
                </Link>
              ))}

              {/* Extra Links */}
              {[
                { to: "/leadership", label: "Leadership" },
                { to: "/stories", label: "Impact Stories" },
                { to: "/faq", label: "80G FAQ" },
              ].map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-[var(--cream-2)] transition"
                >
                  {n.label}
                </Link>
              ))}

              {/* Register Gaushala */}
              <Link
                to="/register-institution"
                onClick={() => setMenuOpen(false)}
                className="mt-1 flex items-center gap-2 rounded-xl bg-[var(--saffron)]/15 border border-[var(--saffron-deep)]/30 px-3 py-2.5 text-xs font-bold text-[var(--maroon-deep)] hover:bg-[var(--saffron)]/25 transition"
              >
                <Building2 className="h-4 w-4" /> Register Institution / Gaushala
              </Link>

              {/* Bottom CTAs */}
              <div className="mt-2 flex gap-2 pb-2">
                <Link
                  to="/demo"
                  onClick={() => setMenuOpen(false)}
                  className="flex-1 rounded-full border border-border px-4 py-2.5 text-center text-xs font-semibold hover:bg-muted transition"
                >
                  Demo Login
                </Link>
                <Link
                  to="/donate"
                  onClick={() => setMenuOpen(false)}
                  className="flex-1 rounded-full bg-[var(--maroon)] px-4 py-2.5 text-center text-xs font-semibold text-[var(--cream)] hover:bg-[var(--maroon-deep)] transition"
                >
                  Donate
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}